import { useEffect, useState } from 'react';
import OneliveMobileDrawerFooter from './OneliveMobileDrawerFooter';
import OneliveMobileDrawerNav from './OneliveMobileDrawerNav';
import { oneliveAssets } from './assets';
import {
  fetchCurrentMemberProfile,
  fetchItemShopWallet,
  formatAmount,
  getCurrentUser,
  logoutAccount,
  navigate,
  navigateToMypageHub,
  normalizeAssetUrl,
  resolveDisplayNickname,
} from './api';
import { DRAWER_QUICK_LINKS, resolveMemberLevel } from './memberLevel';

interface Props {
  open: boolean;
  onClose: () => void;
}

type DrawerUser = {
  uuid?: string;
  name?: string;
  email?: string;
  nickname?: string;
  avatar?: string;
  member_level?: number;
  member_level_label?: string;
};

export default function OneliveMobileChannelDrawer({ open, onClose }: Props) {
  const [user, setUser] = useState<DrawerUser | null>(() => getCurrentUser());
  const [coinBalance, setCoinBalance] = useState(0);
  const [messageCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    const current = getCurrentUser();
    setUser(current);
    if (!current?.uuid) {
      setCoinBalance(0);
      return;
    }
    void fetchItemShopWallet()
      .then((wallet) => setCoinBalance(wallet?.coin_balance ?? 0))
      .catch(() => setCoinBalance(0));
    void fetchCurrentMemberProfile()
      .then((profile) => {
        if (profile) setUser((prev) => ({ ...prev, ...profile }));
      })
      .catch(() => {
        // keep session user
      });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const isLoggedIn = Boolean(user?.uuid);
  const displayName = resolveDisplayNickname(user);
  const memberLevel = resolveMemberLevel(user?.member_level, user?.member_level_label, user);
  const avatarUrl = normalizeAssetUrl(user?.avatar) || oneliveAssets.guestAvatar;

  const go = (path: string) => {
    onClose();
    if (path === '/mypage' || path === '/mypage/profile') {
      navigateToMypageHub();
      return;
    }
    navigate(path);
  };

  const goLogin = () => go('/login');

  const onLogout = async () => {
    onClose();
    await logoutAccount();
    showOneliveToastSafe('로그아웃 되었습니다.');
  };

  return (
    <div className="onelive-mobile-drawer" role="dialog" aria-modal="true" aria-label="모바일 메뉴">
      <div className="onelive-mobile-drawer-backdrop" onClick={onClose} aria-hidden />
      <div className="onelive-mobile-drawer-panel">
        <div className="onelive-mobile-drawer-head onelive-mobile-menu-header">
          <button
            type="button"
            className="onelive-mobile-drawer-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>

          <button
            type="button"
            className="onelive-mobile-drawer-user-link"
            onClick={isLoggedIn ? () => go('/mypage/profile') : goLogin}
          >
            <span className="onelive-mobile-drawer-user-info">
              <span className="onelive-mobile-drawer-user-img" aria-hidden="true">
                {isLoggedIn ? (
                  <img src={avatarUrl} alt="" className="onelive-mobile-drawer-user-guest-img" onError={(e) => { e.currentTarget.src = oneliveAssets.guestAvatar; }} />
                ) : (
                  <img
                    className="onelive-mobile-drawer-user-guest-img"
                    src={oneliveAssets.guestAvatar}
                    alt=""
                    width={56}
                    height={56}
                  />
                )}
              </span>
              <span className="onelive-mobile-drawer-user-txt">
                <em className="point">
                  {isLoggedIn ? displayName : '로그인을 해주세요'}
                </em>
                {isLoggedIn ? (
                  <span className="onelive-mobile-drawer-level" style={{ color: memberLevel.tone }}>
                    <img
                      src={memberLevel.icon}
                      alt=""
                      className="onelive-mobile-drawer-level-icon"
                      onError={(e) => { e.currentTarget.src = oneliveAssets.levelDefault; }}
                    />
                    <span>{memberLevel.label}</span>
                  </span>
                ) : null}
              </span>
            </span>
          </button>

          {isLoggedIn ? (
            <div className="onelive-mobile-drawer-side-cash">
              <div className="onelive-mobile-drawer-cash-main">
                <span className="onelive-mobile-drawer-coin-icon" aria-hidden="true">
                  <img src={oneliveAssets.drawerCoin} alt="" className="onelive-drawer-line-icon" />
                </span>
                <strong className="onelive-mobile-drawer-coin-value">{formatAmount(coinBalance)}</strong>
              </div>
              <button type="button" className="onelive-mobile-drawer-exchange-btn" onClick={() => go('/exchange-request')}>
                환전신청
              </button>
            </div>
          ) : null}
        </div>

        {isLoggedIn ? (
          <div className="onelive-mobile-drawer-quick-grid">
            {DRAWER_QUICK_LINKS.map((item) => (
              <button key={item.path} type="button" className="onelive-mobile-drawer-quick-item" onClick={() => go(item.path)}>
                <img
                  src={item.icon}
                  alt=""
                  className={`onelive-mobile-drawer-quick-icon${item.lineIcon ? ' onelive-drawer-line-icon' : ''}`}
                />
                <span>
                  {item.label}
                  {item.countKey === 'messages' ? ` (${messageCount})` : ''}
                </span>
              </button>
            ))}
          </div>
        ) : null}

        {isLoggedIn ? (
          <button type="button" className="onelive-mobile-drawer-logout" onClick={() => void onLogout()}>
            로그아웃
          </button>
        ) : null}

        <div className="onelive-mobile-drawer-body">
          <OneliveMobileDrawerNav onNavigate={onClose} />
          <OneliveMobileDrawerFooter onNavigate={onClose} />
        </div>
      </div>
    </div>
  );
}

function showOneliveToastSafe(message: string) {
  const core = (window as any).G7Core;
  if (core?.toast?.show) {
    core.toast.show(message, 'info');
  }
}
